import React, { memo } from 'react';
import { Handle, Position, type NodeProps } from '@xyflow/react';
import {
  Hash,
  Tag,
  FolderTree,
  HelpCircle,
  Package,
  ShieldCheck,
  Bot,
  Database,
  FileText,
  Building2,
  Send,
  Zap,
  Globe,
  ChevronDown,
  ChevronRight,
  Loader2,
} from 'lucide-react';
import { cn } from '@/utils/cn';

// ============= Topic Node =============

const nodeTypeIcons: Record<string, React.ElementType> = {
  topic: Hash,
  entity: Tag,
  category: FolderTree,
  faq: HelpCircle,
  product: Package,
  policy: ShieldCheck,
};

export interface TopicNodeData {
  label: string;
  nodeType: string;
  summary?: string | null;
  kbId: string;
  kbName: string;
  color: string;
  [key: string]: unknown;
}

export const TopicNode = memo(({ data, selected }: NodeProps) => {
  const d = data as unknown as TopicNodeData;
  const Icon = nodeTypeIcons[d.nodeType] || FileText;

  return (
    <div
      className={cn(
        'rounded-xl border-2 bg-white shadow-[3px_3px_0px_#1a1a2e] px-3 py-2 min-w-[120px] transition-all',
        selected ? 'ring-2 ring-blue-500 border-blue-400' : 'border-[#1a1a2e] hover:shadow-[4px_4px_0px_#1a1a2e]'
      )}
    >
      <Handle type="target" position={Position.Top} className="!bg-gray-300 !w-1.5 !h-1.5" />
      <div className="flex items-center gap-2">
        <div
          className="w-6 h-6 rounded flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: d.color + '20' }}
        >
          <Icon className="w-3.5 h-3.5" style={{ color: d.color }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-gray-900 truncate max-w-[120px]">{d.label}</p>
          <p className="text-[10px] text-gray-400 capitalize">{d.nodeType}</p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-gray-300 !w-1.5 !h-1.5" />
    </div>
  );
});

TopicNode.displayName = 'TopicNode';

// ============= KB Node =============

export interface KBNodeData {
  label: string;
  description?: string | null;
  kbType?: string;
  nodeCount: number;
  color: string;
  expanded?: boolean;
  expandLoading?: boolean;
  onToggleExpand?: () => void;
  [key: string]: unknown;
}

export const KBNode = memo(({ data }: NodeProps) => {
  const d = data as unknown as KBNodeData;
  const ExpandIcon = d.expandLoading ? Loader2 : d.expanded ? ChevronDown : ChevronRight;

  return (
    <div
      className={cn(
        'rounded-xl border-2 bg-white shadow-[5px_5px_0px_#1a1a2e] px-4 py-3 min-w-[160px] transition-all',
        d.expanded ? 'ring-1' : ''
      )}
      style={{ borderColor: d.color, ...(d.expanded ? { ringColor: d.color } : {}) }}
    >
      <Handle type="target" position={Position.Top} className="!bg-gray-300 !w-2 !h-2" />
      <div className="flex items-center gap-2">
        <div
          className="w-8 h-8 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: d.color + '20' }}
        >
          <Database className="w-4 h-4" style={{ color: d.color }} />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 truncate max-w-[120px]">{d.label}</p>
          <p className="text-xs text-gray-500">{d.nodeCount} topics</p>
        </div>
        {d.nodeCount > 0 && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              d.onToggleExpand?.();
            }}
            className="w-6 h-6 rounded-md flex items-center justify-center hover:bg-gray-100 transition-colors"
            title={d.expanded ? 'Collapse topics' : 'Expand topics'}
          >
            <ExpandIcon
              className={cn('w-4 h-4 text-gray-500', d.expandLoading && 'animate-spin')}
            />
          </button>
        )}
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-gray-300 !w-2 !h-2" />
    </div>
  );
});

KBNode.displayName = 'KBNode';

// ============= Agent Node =============

export interface AgentNodeData {
  label: string;
  agentType: string;
  status: string;
  [key: string]: unknown;
}

const agentStatusColors: Record<string, string> = {
  active: '#10b981',
  testing: '#f59e0b',
  draft: '#9ca3af',
  paused: '#9ca3af',
  archived: '#6b7280',
};

export const AgentNode = memo(({ data, selected }: NodeProps) => {
  const d = data as unknown as AgentNodeData;
  const statusColor = agentStatusColors[d.status] || '#9ca3af';

  return (
    <div
      className={cn(
        'rounded-xl border-2 bg-white shadow-[5px_5px_0px_#1a1a2e] px-4 py-3 min-w-[150px]',
        selected ? 'ring-2 ring-amber-500 border-amber-400' : 'border-amber-300'
      )}
    >
      <Handle type="target" position={Position.Top} className="!bg-amber-400 !w-2 !h-2" />
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
          <Bot className="w-4 h-4 text-amber-600" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 truncate max-w-[120px]">{d.label}</p>
          <div className="flex items-center gap-1.5">
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusColor }} />
            <p className="text-[10px] text-gray-400 capitalize">{d.agentType.replace(/_/g, ' ')}</p>
          </div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-amber-400 !w-2 !h-2" />
    </div>
  );
});

AgentNode.displayName = 'AgentNode';

// ============= Organization Node =============

export interface OrgNodeData {
  label: string;
  [key: string]: unknown;
}

export const OrgNode = memo(({ data, selected }: NodeProps) => {
  const d = data as unknown as OrgNodeData;

  return (
    <div
      className={cn(
        'rounded-xl border-2 bg-slate-800 shadow-[5px_5px_0px_#1a1a2e] px-5 py-4 min-w-[180px]',
        selected ? 'ring-2 ring-blue-400 border-blue-400' : 'border-slate-600'
      )}
    >
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center">
          <Building2 className="w-5 h-5 text-white" />
        </div>
        <div className="min-w-0">
          <p className="text-sm font-bold text-white truncate max-w-[140px]">{d.label}</p>
          <p className="text-[10px] text-slate-400">Organization</p>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-slate-400 !w-2.5 !h-2.5" />
    </div>
  );
});

OrgNode.displayName = 'OrgNode';

// ============= Channel Node =============

export interface ChannelNodeData {
  label: string;
  channelType: string;
  status: string;
  [key: string]: unknown;
}

const channelTypeConfig: Record<string, { icon: React.ElementType; bg: string; border: string; iconColor: string }> = {
  telegram: { icon: Send, bg: 'bg-sky-50', border: 'border-sky-300', iconColor: 'text-sky-600' },
  facebook: { icon: Globe, bg: 'bg-blue-50', border: 'border-blue-300', iconColor: 'text-blue-600' },
  instagram: { icon: Globe, bg: 'bg-purple-50', border: 'border-purple-300', iconColor: 'text-purple-600' },
  custom: { icon: Zap, bg: 'bg-indigo-50', border: 'border-indigo-300', iconColor: 'text-indigo-600' },
};

const channelStatusColors: Record<string, string> = {
  connected: '#10b981',
  active: '#10b981',
  disconnected: '#9ca3af',
  error: '#ef4444',
};

export const ChannelNode = memo(({ data, selected }: NodeProps) => {
  const d = data as unknown as ChannelNodeData;
  const config = channelTypeConfig[d.channelType] || channelTypeConfig.custom;
  const Icon = config.icon;
  const statusColor = channelStatusColors[d.status] || '#9ca3af';

  return (
    <div
      className={cn(
        'rounded-xl border-2 bg-white shadow-[4px_4px_0px_#1a1a2e] px-4 py-2.5 min-w-[140px]',
        selected ? 'ring-2 ring-blue-500' : '',
        config.border
      )}
    >
      <Handle type="target" position={Position.Top} className="!bg-gray-300 !w-2 !h-2" />
      <div className="flex items-center gap-2">
        <div className={cn('w-7 h-7 rounded-lg flex items-center justify-center', config.bg)}>
          {d.channelType === 'facebook' ? (
            <span className="text-sm">👤</span>
          ) : d.channelType === 'instagram' ? (
            <span className="text-sm">📷</span>
          ) : (
            <Icon className={cn('w-3.5 h-3.5', config.iconColor)} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold text-gray-900 truncate max-w-[110px]">{d.label}</p>
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: statusColor }} />
            <p className="text-[10px] text-gray-400 capitalize">{d.channelType}</p>
          </div>
        </div>
      </div>
      <Handle type="source" position={Position.Bottom} className="!bg-gray-300 !w-2 !h-2" />
    </div>
  );
});

ChannelNode.displayName = 'ChannelNode';
